import { createPortal } from 'react-dom';

import { useFeatureAnnouncementTargetRect } from '../../_hooks';

// Dims the page and leaves the announced element lit, so the eye lands on the one control the
// tooltip is about instead of hunting the screen for whatever the arrow points at.
//
// The hole is a box-shadow with an absurd spread rather than four rectangles laid around the
// target: one element, no hairline seams where the pieces would meet, and the corners of the
// hole can be rounded. The ring is a second shadow on the same element, which is why it paints
// over the dim rather than under it.
//
// This also *is* the modal barrier. It covers the viewport and takes every click, so while a
// tooltip is up the only things on the page that respond are the card's own buttons — which is
// the whole reason the page reads as blocked rather than merely tinted. The two painted
// children are `pointer-events-none` so the click lands on this element and stops there,
// including over the spotlit control: it is lit to be read, not to be pressed, and pressing it
// is what `Got It` is for.
//
// Deliberately not Radix's `modal` popover, which would get the same blocking by setting
// `pointer-events: none` on `document.body`. This component is mounted on every authenticated
// page, and a cleanup that failed to run there does not degrade the tooltip, it freezes the
// app. A div that unmounts with its owner cannot do that.
const SPOTLIGHT_PADDING = 6;

const FeatureAnnouncementSpotlight = ({ element }) => {
  const rect = useFeatureAnnouncementTargetRect(element);

  if (!rect) {
    return null;
  }

  const hole = {
    top: rect.top - SPOTLIGHT_PADDING,
    left: rect.left - SPOTLIGHT_PADDING,
    width: rect.width + SPOTLIGHT_PADDING * 2,
    height: rect.height + SPOTLIGHT_PADDING * 2
  };

  return createPortal(
    <div className="fixed inset-0 z-50" data-feature-announcement-layer="" data-testid="feature-announcement-spotlight">
      {/*
        80% black, written out rather than built from a constant: Tailwind reads class names
        out of the source text, so an interpolated one generates no CSS at all. The 55% this
        started at left the page behind legible, which made the dim read as a tint over a page
        you could still work in rather than as a barrier you have to answer.
      */}
      <div className="animate-in fade-in-0 pointer-events-none absolute rounded-md shadow-[0_0_0_9999px_rgb(0_0_0/0.8)] duration-200" style={hole} />

      {/* The ring is its own element so that it can pulse without taking the dim with it. */}
      <div className="pointer-events-none absolute animate-pulse rounded-md ring-2 ring-primary/70" style={hole} />
    </div>,
    document.body
  );
};

export default FeatureAnnouncementSpotlight;
